//deps
import React, { memo, ReactElement, ReactNode, useCallback, useContext, useMemo, useRef } from "react";

//internal-deps
import { format, isMWebPlatform, withStyles } from "@swiggy-private/core-ui";
import { DineoutCheckoutRoute } from "@swiggy-private/checkout-types";
import {
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    GlobalHeaderState,
    recordNREvent,
    SwgyHeaderKeysList,
    useNavigate,
    closeWebview,
    NEWRELIC_TABLES,
    getBridgeSdk,
    useCustomHardwareBack,
    OrderInterruptionAlertPayload,
    goToHome,
    DineoutCartContext,
    DineoutCartType,
    navigateToCart,
    getNativeChannelDeepLink,
    MROFoodCartActionPayload,
    useQuickCheckoutNavHandlers,
    isQuickCheckoutFlow,
} from "@swiggy-private/checkout-core";
//helpers
import {
    getExperimentValue,
    getPaymentHeaderDiscountMessage,
    getPaymentHeaderTitleV4,
} from "@payment-client/helpers/PaymentHelper";
import { BOOLEAN_AS_STRING } from "@swiggy-private/payment-core";
import {
    getFeedbackUrl,
    getPaymentFeedbackFlag,
    updatePaymentFeedbackFlag,
} from "@payment-client/helpers/PaymentFeedback";
import {
    isBusinessLineRegular,
    isBusinessLineSwiggyMoney,
    isScenesFullPaymentFlow,
} from "@payment-client/helpers/Misc";

// styles
import styles from "./PaymentUIWrapper.scss";

//components
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import HeaderHelpButton from "./HelpButton";

//contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

//constants
import { EXPERIMENTS_VALUES } from "@payment-client/constants/Experiments";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import { PAYMENT_FEEDBACK_DEEPLINK } from "@payment-client/constants/PaymentFeedback";
import { GIFT_DETAILS_STORAGE_KEY } from "@payment-client/constants/StorageKeys";
import { ORDER_TERMINAL_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { PAYMENT_INITIAL_STATE } from "@payment-client/components/payment-main";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

// import useRestoreScroll from "@payment-client/hooks/UseRestoreScroll";

interface PaymentUIWrapperProps {
    children: ReactNode;
    withCoupon: boolean;
    setWithCoupon: (coupon: boolean) => void;
    couponApplied?: boolean;
    quantity?: number;
    amount?: string | number;
}

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

function PaymentsHeaderContent(
    props: Omit<PaymentUIWrapperProps, "children" | "setWithCoupon" | "couponApplied">,
): ReactElement {
    const { checkoutMetaInfo, paymentResponse } = useContext(ShowPaymentContext);

    const title = useMemo(
        () => getPaymentHeaderTitleV4(paymentResponse?.presentationDetails),
        [paymentResponse?.presentationDetails],
    );

    const offerText = getPaymentHeaderDiscountMessage({
        checkoutMetaInfo,
        presentationDetails: paymentResponse?.presentationDetails,
    });

    return (
        <CommonHeaderContent
            title={title}
            grayscale={isBusinessLineSwiggyMoney()}
            quantity={props.quantity}
            discountMessage={offerText}
        />
    );
}

function PaymentsUIWrapper({
    children,
    setWithCoupon,
    withCoupon,
    couponApplied,
    ...props
}: PaymentUIWrapperProps): React.ReactElement {
    const isSwiggyMoneyFlow = isBusinessLineSwiggyMoney();
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const errorStatusCode = (checkoutState?.payload as OrderInterruptionAlertPayload)?.errorStatusCode;
    const navigateTo = useNavigate();
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { cartType: dineoutCartType } = useContext(DineoutCartContext);
    const { handlePaymentsToQuickCheckout } = useQuickCheckoutNavHandlers();

    const openPaymentFeedbackWebview = useCallback(() => {
        const url = getFeedbackUrl();
        const nativePaymentFeedbackDeeplink = getNativeChannelDeepLink(PAYMENT_FEEDBACK_DEEPLINK);

        getBridgeSdk()
            .navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: format(nativePaymentFeedbackDeeplink, encodeURIComponent(url)),
            })
            .catch(() => void 0);
    }, []);

    const showPaymentFeedback = useMemo(
        () =>
            (getExperimentValue(
                paymentResponse,
                EXPERIMENTS_VALUES.PAYMENT_ABANDONMENT_FEEDBACK,
            ) as BOOLEAN_AS_STRING) === BOOLEAN_AS_STRING.TRUE,
        [paymentResponse],
    );

    const getIsConfirmOrderFlow = () => {
        if (typeof window === "undefined") {
            return false;
        }

        const urlParams = new URLSearchParams(window?.location?.search);
        const state = urlParams.get("state");
        const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
        return state === PAYMENT_INITIAL_STATE?.CONFIRM_ORDER && isMWebPlatform(platform);
    };

    const isConfirmOrderFlow = getIsConfirmOrderFlow();

    const _onBack = useCallback(async () => {
        if (
            checkoutState.type === CheckoutActionTypes.ORDER_INTERRUPTION &&
            errorStatusCode === ORDER_TERMINAL_STATUS_CODE
        ) {
            goToHome();
            return;
        }

        // TODO - needs cleanup after feature release
        if (isSwiggyMoneyFlow && isSessionStorageAvailable && !sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY)) {
            navigateTo(CheckoutRoute.ADD_SWIGGY_MONEY, {}, true);
            return;
        }

        if (!withCoupon && couponApplied) {
            setWithCoupon(true);
            return;
        }

        const isPaymentFeedbackShown = await getPaymentFeedbackFlag();
        if (showPaymentFeedback && !isPaymentFeedbackShown && isBusinessLineRegular()) {
            openPaymentFeedbackWebview();

            // updating payment feedback storage key
            updatePaymentFeedbackFlag();
            return;
        }

        if (isSessionStorageAvailable && sessionStorage.getItem("isFromWebCart") === "true" && !isConfirmOrderFlow) {
            setCheckoutState({ type: CheckoutActionTypes.SHOW_CART, payload: null });
            navigateToCart(navigateTo, {}, false);
        } else if (isSessionStorageAvailable && sessionStorage.getItem("isFromGiftingCart") === "true") {
            setCheckoutState({ type: CheckoutActionTypes.SHOW_GIFTING_CART, payload: null });
            navigateTo(CheckoutRoute.GIFTING_CART, {}, false);
        } else if (
            isSessionStorageAvailable &&
            sessionStorage.getItem("isFromMROFoodCart") === "true" &&
            isQuickCheckoutFlow()
        ) {
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
                payload: { refreshCartDataForQuickCheckout: true },
            });
            navigateTo(CheckoutRoute.MRO_FOOD_CART, {}, false);
        } else if (isSessionStorageAvailable && sessionStorage.getItem("isFromMROFoodCart") === "true") {
            // Signal the MRO cart that we are returning from payments so it can reuse the
            // previously loaded cart for tabs instead of running the full refetch pipeline.
            // The actual TTL check and reuse logic live in the MRO cart fetch hook.
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
                payload: { skipTabsRefetch: true } as MROFoodCartActionPayload,
            });
            navigateTo(CheckoutRoute.MRO_FOOD_CART, {}, false);
        } else if (isSessionStorageAvailable && sessionStorage.getItem("isFromQuickCheckout") === "true") {
            handlePaymentsToQuickCheckout();
        } else if (isSessionStorageAvailable && sessionStorage.getItem("isFromDineoutCart") === "true") {
            // This is needed because scenes full payment flow should exit the payment screen
            // and return to the success page when the back button is pressed instead of the payment screen
            if (isScenesFullPaymentFlow()) {
                closeWebview();
                return;
            }

            if (dineoutCartType === DineoutCartType.BILL_PAYMENT_CART) {
                setCheckoutState({
                    type: CheckoutActionTypes.DINEOUT_BILL_PAYMENT_CART,
                    payload: {},
                });
                navigateTo(DineoutCheckoutRoute.BILL_PAYMENT_CART, {}, false);
            } else if (dineoutCartType === DineoutCartType.STEAL_STORE_CART) {
                setCheckoutState({
                    type: CheckoutActionTypes.STEAL_STORE_CART,
                    payload: null,
                });
                navigateTo(DineoutCheckoutRoute.STEAL_STORE_CART, {}, false);
            } else if (
                dineoutCartType === DineoutCartType.EVENT_BOOKING_CART ||
                dineoutCartType === DineoutCartType.TABLE_RESERVATION_CART
            ) {
                setCheckoutState({ type: CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART });
                navigateTo(DineoutCheckoutRoute.TABLE_RESERVATION_CART, {}, false);
            }
        } else {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEB_CHECKOUT_BACK_BUTTON_PAYMENT_SCREEN,
                isSessionStorageAvailable: String(isSessionStorageAvailable),
                isFromBottomBar: GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar),
                referrerScreen: GlobalHeaderState.getHeader(SwgyHeaderKeysList.referrer),
            });
            closeWebview();
        }
    }, [
        checkoutState.type,
        errorStatusCode,
        isSwiggyMoneyFlow,
        isSessionStorageAvailable,
        withCoupon,
        couponApplied,
        showPaymentFeedback,
        navigateTo,
        setWithCoupon,
        openPaymentFeedbackWebview,
        setCheckoutState,
        dineoutCartType,
        handlePaymentsToQuickCheckout,
    ]);

    useCustomHardwareBack([CheckoutRoute.PAYMENTS, CheckoutRoute.SWIGGY_MONEY_PAYMENTS], _onBack);

    const containerRef = useRef<ScrollableDivElement>(null);
    // useRestoreScroll(containerRef, "prev_scroll_position");

    const theme = useTheme();

    return (
        <ThemeProvider theme={theme} colorMode={"light"} design_language={theme.designLanguage}>
            <CommonUIWrapper
                onBack={_onBack}
                header={<PaymentsHeaderContent {...props} withCoupon={withCoupon} />}
                headerExtraContent={<HeaderHelpButton />}
                content={children}
                key={withCoupon ? 1 : 0}
                ref={containerRef}
                coreLayoutClass={styles.containerLayout}
            />
        </ThemeProvider>
    );
}

export default memo(withStyles(PaymentsUIWrapper, styles));
